/**
 * screens/aiUsage/aiUsage.tsx
 * AI usage — the full-screen detail behind the sidebar credits card. Wired to
 * GET /api/hire/v2/ai/usage. Premium, animated, grayscale:
 *   • KPI tiles (total / input / output tokens · calls · rerank runs) with count-up
 *   • a daily comparison chart (Input vs Output, or By purpose) — bars grow on mount,
 *     hover for a per-day tooltip
 *   • breakdowns by purpose, model and user (animated meters)
 *   • a paginated, filterable log of every metered AI call (op · model · tokens ·
 *     user · time), live over the same SSE rev the sidebar uses.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, StyleSheet, Platform } from 'react-native';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';

import { useTheme } from '../../context/themeContext';
import { useBreakpoints, ContentMaxWidth } from '../../theme/responsive';
import { navigateApp } from '../../navigation/navigationRef';
import { web } from '../../utils/web';
import { useHover } from '../../hooks/useHover';
import { useWorkspaceStats } from '../../hooks/useAiUsage';
import { Icon, Panel, Pagination, EmptyState } from '../../components/ui';
import { getAiUsageDetail } from '../../services/hire/aiUsage';
import type { AiUsageDetail, AiUsageOpFilter, AiUsageSeriesPoint } from '../../schemas/hire/aiUsage';

/* ── premium helpers ──────────────────────────────────────────────────── */

function rise(delay = 0): object {
  return web({ animation: 'sk-rise 480ms cubic-bezier(.2,.8,.2,1) both', animationDelay: `${delay}ms` });
}

/** True one frame after mount — lets CSS transitions animate from 0 → target. */
function useMounted(): boolean {
  const [m, setM] = useState(Platform.OS !== 'web');
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const raf = requestAnimationFrame(() => setM(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  return m;
}

/** Animate a number 0 → target on change (web only). */
function useCountUp(target: number, duration = 640): number {
  const [n, setN] = useState(Platform.OS === 'web' ? 0 : target);
  useEffect(() => {
    if (Platform.OS !== 'web') { setN(target); return; }
    let raf = 0;
    const start = performance.now();
    const from = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

/* ── op (purpose) metadata ────────────────────────────────────────────── */

const OP_META: Record<string, { label: string; short: string; icon: string }> = {
  jd_extract:     { label: 'Job JD extraction',   short: 'Job',       icon: 'layers' },
  resume_extract: { label: 'Resume extraction',   short: 'Candidate', icon: 'user' },
  rerank:         { label: 'Match run (rerank)',  short: 'Match run', icon: 'sparkles' },
  skill_curate:   { label: 'Skill learning',      short: 'Taxonomy',  icon: 'zap' },
  // One cached verdict per distinct (requirement, qualifications) pair — shared
  // across candidates and jobs, so this line stays small even on a big pool.
  education_check: { label: 'Education / licence check', short: 'Education', icon: 'shield' },
};
const SOURCE_META: Record<string, { label: string; short: string; icon: string }> = {
  bulk_import: { label: 'Bulk import',            short: 'Bulk import', icon: 'upload' },
  single:      { label: 'One at a time',          short: 'Single',      icon: 'user' },
  background:  { label: 'System · background job', short: 'System',     icon: 'zap' },
  unknown:     { label: 'Before channel tracking', short: 'Unknown',    icon: 'info' },
};
function opMeta(op: string) {
  if (op.startsWith('source:')) return SOURCE_META[op.slice(7)] ?? { label: op, short: op, icon: 'upload' };
  return OP_META[op] ?? { label: op, short: op, icon: 'zap' };
}
export function sourceMeta(src: string) { return SOURCE_META[src] ?? { label: src, short: src, icon: 'zap' }; }

const OP_FILTERS: { key: AiUsageOpFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'jd_extract', label: 'Job' },
  { key: 'resume_extract', label: 'Candidate' },
  { key: 'rerank', label: 'Match run' },
  { key: 'skill_curate', label: 'Taxonomy' },
  // Channel, not purpose: a bulk run spends on the same extraction ops as a
  // single upload, so it can only be separated by where it came from.
  { key: 'source:bulk_import', label: 'Bulk import' },
];
const RANGES: { key: number; label: string }[] = [
  { key: 7, label: '7d' },
  { key: 30, label: '30d' },
  { key: 90, label: '90d' },
];

/** Exact calendar date, e.g. "21 Jul 2026". */
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}
/** Exact wall-clock time incl. seconds, e.g. "14:32:07". */
function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}
/** Full exact timestamp for tooltips. */
function fmtDateTime(iso: string): string { return `${fmtDate(iso)} · ${fmtTime(iso)}`; }
function fmt(n: number): string { return n.toLocaleString(); }
/** Compact axis label, e.g. 10000 → "10k", 2500 → "2.5k". */
function fmtCompact(n: number): string {
  if (n >= 1000) { const k = n / 1000; return `${Number.isInteger(k) ? k : k.toFixed(1)}k`; }
  return String(Math.round(n));
}
/** Round a value up to a "nice" axis ceiling (1/2/2.5/5/10 × 10ⁿ). */
function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  const m = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return m * p;
}

// Shared log-table column geometry so the header and body rows are laid out by
// the SAME definitions and can never drift. Right-aligned numeric columns use
// alignItems on the wrapper (not textAlign) so header + values share an edge.
const GAP = 16;
type Align = 'left' | 'right';
const COLUMNS: { key: string; label: string; flex?: number; width?: number; align: Align; padLeft?: number }[] = [
  { key: 'sn', label: 'S.No', width: 44, align: 'left' },
  { key: 'purpose', label: 'Purpose', flex: 1.6, align: 'left' },
  { key: 'ref', label: 'Ref ID', flex: 1.05, align: 'left' },
  { key: 'model', label: 'Model', flex: 1.2, align: 'left' },
  { key: 'input', label: 'Input', flex: 0.85, align: 'right' },
  { key: 'output', label: 'Output', flex: 0.85, align: 'right' },
  { key: 'total', label: 'Total', flex: 0.8, align: 'right' },
  { key: 'user', label: 'User', flex: 1.5, align: 'left', padLeft: 20 },
  { key: 'time', label: 'Exact time', width: 120, align: 'right' },
];
/** Short, stable reference derived from the ledger row's DB id (no hashes). */
function shortRef(id: string): string { return id.replace(/-/g, '').slice(0, 8).toUpperCase(); }
const col = (k: string) => COLUMNS.find((c) => c.key === k)!;
function colStyle(c: { flex?: number; width?: number; align: Align; padLeft?: number }): object {
  return {
    ...(c.width != null ? { width: c.width } : { flex: c.flex }),
    minWidth: 0,
    alignItems: c.align === 'right' ? 'flex-end' : 'flex-start',
    ...(c.padLeft ? { paddingLeft: c.padLeft } : {}),
  };
}

/* ── small controls ───────────────────────────────────────────────────── */

function Segmented<T extends string | number>({
  options, value, onChange,
}: { options: { key: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  return (
    <View style={{ flexDirection: 'row', backgroundColor: colors.paper2, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, padding: 3, gap: 2 }}>
      {options.map((o) => {
        const active = o.key === value;
        return (
          <Pressable
            key={String(o.key)}
            onPress={() => onChange(o.key)}
            style={[
              { paddingHorizontal: 12, height: 28, borderRadius: radii.sm, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? colors.card : 'transparent' },
              web({ cursor: 'pointer', transition: 'background 150ms ease' }),
              active && Platform.OS === 'web' ? ({ boxShadow: '0 1px 3px rgba(0,0,0,0.14)' } as object) : null,
            ]}
          >
            <Text style={{ fontSize: fontSize.sm, fontWeight: active ? fontWeight.semibold : fontWeight.medium, color: active ? colors.ink : colors.ink3 }}>{o.label}</Text>
          </Pressable>
        );
      })}
    </View>
  );
}

/* ── KPI tile ─────────────────────────────────────────────────────────── */

function Kpi({
  icon, label, value, sub, emphasis, delay,
}: { icon: string; label: string; value: number; sub?: string; emphasis?: boolean; delay: number }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const n = useCountUp(value);
  return (
    <View
      {...hoverProps}
      style={[
        {
          flex: 1, minWidth: 150, paddingVertical: 16, paddingHorizontal: 18, borderRadius: radii.lg, borderWidth: 1,
          borderColor: hovered ? colors.lineStrong : colors.line,
          backgroundColor: emphasis ? colors.accentSoft : colors.card,
        },
        rise(delay),
        web({ transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease' }),
        hovered && web({ transform: 'translateY(-3px)', boxShadow: '0 12px 30px -18px rgba(0,0,0,0.4)' }),
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <View style={{ width: 26, height: 26, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: emphasis ? colors.accent : colors.paper2, borderWidth: emphasis ? 0 : 1, borderColor: colors.line }}>
          <Icon name={icon} size={14} color={emphasis ? colors.onAccent : colors.ink3} />
        </View>
        <Text style={{ fontSize: 11, fontWeight: fontWeight.semibold, letterSpacing: 0.4, textTransform: 'uppercase', color: colors.ink4 }}>{label}</Text>
      </View>
      <Text style={{ fontSize: 26, fontWeight: fontWeight.bold, color: emphasis ? colors.accentInk : colors.ink, letterSpacing: -0.6 }}>{fmt(n)}</Text>
      {sub ? <Text style={{ fontSize: fontSize.xs, color: colors.ink3, marginTop: 3 }}>{sub}</Text> : null}
    </View>
  );
}

/* ── comparison chart ─────────────────────────────────────────────────── */

// Chart views: input-vs-output, all-purposes stacked, or a single purpose
// focused (Job extraction / Candidate extraction / Match run).
type ChartMode = 'io' | 'op' | 'jd_extract' | 'resume_extract' | 'rerank';
const CHART_MODES: { key: ChartMode; label: string }[] = [
  { key: 'io', label: 'In vs Out' },
  { key: 'op', label: 'By purpose' },
  { key: 'jd_extract', label: 'Job' },
  { key: 'resume_extract', label: 'Candidate' },
  { key: 'rerank', label: 'Match run' },
];
/** Tokens represented by a bar under the current chart mode. */
function pointTotalFor(point: AiUsageSeriesPoint, mode: ChartMode): number {
  if (mode === 'io') return point.totalTokens;
  if (mode === 'op') return Object.values(point.ops).reduce((a, b) => a + b, 0);
  return point.ops[mode] ?? 0;
}

function ChartColumn({
  point, max, mode, height, colorsBy, mounted, index, showLabel, tipAlign,
}: {
  point: AiUsageSeriesPoint; max: number; mode: ChartMode; height: number;
  colorsBy: { input: string; output: string; ops: Record<string, string> };
  mounted: boolean; index: number; showLabel: boolean; tipAlign: 'left' | 'center' | 'right';
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();

  const segments = mode === 'io'
    ? [
        { key: 'input', value: point.inputTokens, color: colorsBy.input },
        { key: 'output', value: point.outputTokens, color: colorsBy.output },
      ]
    : mode === 'op'
      ? Object.keys(OP_META)
          .map((op) => ({ key: op, value: point.ops[op] ?? 0, color: colorsBy.ops[op] }))
          .filter((s) => s.value > 0)
      : [{ key: mode, value: point.ops[mode] ?? 0, color: colorsBy.ops[mode] ?? colorsBy.input }].filter((s) => s.value > 0);

  const scale = (v: number) => (max > 0 ? (v / max) * height : 0);
  const total = pointTotalFor(point, mode);
  // Topmost non-zero segment gets the rounded cap.
  const firstNonZero = segments.findIndex((s) => s.value > 0);

  return (
    <View {...hoverProps} style={[{ flex: 1, alignItems: 'center', justifyContent: 'flex-end', minWidth: 4, height }, web({ cursor: total > 0 ? 'pointer' : 'default' })]}>
      {/* Tooltip */}
      {hovered && total > 0 && (
        <View
          pointerEvents="none"
          style={[
            { position: 'absolute', bottom: '100%', marginBottom: 8, width: 172, padding: 10, borderRadius: radii.md, borderWidth: 1, borderColor: colors.lineStrong, backgroundColor: colors.card, zIndex: 30 },
            // Anchor inward so edge columns never clip past the panel bounds.
            tipAlign === 'left' ? { left: 0 } : tipAlign === 'right' ? { right: 0 } : ({ left: '50%', marginLeft: -86 } as object),
            Platform.OS === 'web' ? ({ boxShadow: '0 14px 34px -16px rgba(0,0,0,0.5)' } as object) : null,
          ]}
        >
          <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink, marginBottom: 6 }}>
            {new Date(point.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
          </Text>
          {mode === 'io' ? (
            <>
              <TooltipRow dot={colorsBy.input} label="Input tokens" value={point.inputTokens} />
              <TooltipRow dot={colorsBy.output} label="Output tokens" value={point.outputTokens} />
            </>
          ) : mode === 'op' ? (
            Object.keys(OP_META).map((op) => (
              <TooltipRow key={op} dot={colorsBy.ops[op]} label={opMeta(op).short} value={point.ops[op] ?? 0} />
            ))
          ) : (
            <TooltipRow dot={colorsBy.ops[mode] ?? colorsBy.input} label={`${opMeta(mode).short} tokens`} value={point.ops[mode] ?? 0} />
          )}
          <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 6 }} />
          <TooltipRow label="Total tokens" value={total} bold />
        </View>
      )}

      {/* Value label above the bar (only for real data, when there's room). */}
      {showLabel && total > 0 && (
        <Text style={{ fontFamily: font.mono, fontSize: 10, fontWeight: fontWeight.semibold, color: hovered ? colors.ink : colors.ink3, marginBottom: 4 }}>
          {fmtCompact(total)}
        </Text>
      )}

      {/* Bar */}
      <View style={{ width: '70%', maxWidth: 22, justifyContent: 'flex-end' }}>
        {segments.map((s, i) => (
          <View
            key={s.key}
            style={[
              {
                height: mounted ? (s.value > 0 ? Math.max(3, scale(s.value)) : 0) : 0,
                backgroundColor: s.color,
                borderTopLeftRadius: i === firstNonZero ? 4 : 0,
                borderTopRightRadius: i === firstNonZero ? 4 : 0,
                opacity: hovered ? 1 : 0.95,
              },
              web({ transition: `height 640ms cubic-bezier(.2,.8,.2,1) ${index * 10}ms, opacity 150ms ease` }),
            ]}
          />
        ))}
      </View>

      {/* Empty-day baseline tick (so gaps read as "no usage", not missing). */}
      {total === 0 && <View pointerEvents="none" style={{ width: 4, height: 3, borderRadius: 2, backgroundColor: colors.line }} />}
    </View>
  );
}

function TooltipRow({ dot, label, value, bold }: { dot?: string; label: string; value: number; bold?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 1 }}>
      {dot ? <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: dot }} /> : <View style={{ width: 7 }} />}
      <Text style={{ fontSize: fontSize.xs, color: colors.ink3, flex: 1 }}>{label}</Text>
      <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, fontWeight: bold ? fontWeight.bold : fontWeight.semibold, color: colors.ink2 }}>{fmt(value)}</Text>
    </View>
  );
}

/* ── breakdown meter row ──────────────────────────────────────────────── */

function MeterRow({
  icon, label, value, total, color, delay, mono,
}: { icon?: string; label: string; value: number; total: number; color: string; delay: number; mono?: string }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const mounted = useMounted();
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <View style={[{ gap: 6 }, rise(delay)]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        {icon ? (
          <View style={{ width: 22, height: 22, borderRadius: 7, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
            <Icon name={icon} size={12} color={colors.ink3} />
          </View>
        ) : null}
        <Text style={{ flex: 1, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium }} numberOfLines={1}>{label}</Text>
        <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }}>{mono ?? fmt(value)}</Text>
        <Text style={{ fontSize: fontSize.xs, color: colors.ink4, width: 38, textAlign: 'right' }}>{pct}%</Text>
      </View>
      <View style={{ height: 7, borderRadius: 999, backgroundColor: colors.paper3, overflow: 'hidden' }}>
        <View style={[{ width: mounted ? `${pct}%` : '0%', height: '100%', backgroundColor: color, borderRadius: 999 }, web({ transition: `width 700ms cubic-bezier(.2,.8,.2,1) ${delay}ms` })]} />
      </View>
    </View>
  );
}

/* ── log row ──────────────────────────────────────────────────────────── */

function LogRow({ entry, serial }: { entry: AiUsageDetail['entries']['items'][number]; serial: number }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const { isMobile } = useBreakpoints();
  const meta = opMeta(entry.op);
  const person = entry.email ?? 'Unattributed';

  // Mobile: a stacked card. The 9-column log can't fit at phone widths (columns
  // collapse and headers wrap char-by-char), so each entry reflows into an
  // identity header + a labeled meta list (same style as the desktop hover card).
  if (isMobile) {
    return (
      <View style={{ padding: 14, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, gap: 10 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink4, minWidth: 18 }}>{serial}</Text>
          <View style={{ width: 30, height: 30, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
            <Icon name={meta.icon} size={15} color={colors.ink2} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }} numberOfLines={1}>{meta.short}</Text>
            <Text style={{ fontSize: 11, color: colors.ink4 }} numberOfLines={1}>{meta.label}</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontFamily: font.mono, fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{fmt(entry.totalTokens)}</Text>
            <Text style={{ fontSize: 10, color: colors.ink4 }}>tokens</Text>
          </View>
        </View>
        <View style={{ height: 1, backgroundColor: colors.line }} />
        <DetailRow label="Ref ID" value={shortRef(entry.id)} icon="tag" mono />
        <DetailRow label="Model" value={entry.model ?? '—'} icon="box" mono />
        <DetailRow label="Input" value={fmt(entry.inputTokens)} mono />
        <DetailRow label="Output" value={fmt(entry.outputTokens)} mono />
        <DetailRow label="Triggered by" value={person} icon="user" />
        <DetailRow label="Exact time" value={fmtDateTime(entry.createdAt)} icon="clock" />
      </View>
    );
  }

  return (
    <View
      {...hoverProps}
      style={[
        { flexDirection: 'row', alignItems: 'center', gap: GAP, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: colors.line, backgroundColor: hovered ? colors.paper2 : 'transparent', position: 'relative' },
        web({ transition: 'background 130ms ease' }),
        hovered ? ({ zIndex: 30 } as object) : null,
      ]}
    >
      {/* Rich hover detail — who ran what, exact tokens by direction, purpose, time. */}
      {hovered && (
        <View
          pointerEvents="none"
          style={[
            { position: 'absolute', left: 52, bottom: '92%', width: 300, padding: 14, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.lineStrong, backgroundColor: colors.card, zIndex: 40 },
            Platform.OS === 'web' ? ({ boxShadow: '0 18px 44px -18px rgba(0,0,0,0.6)' } as object) : null,
          ]}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 10 }}>
            <View style={{ width: 32, height: 32, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.accent }}>
              <Icon name={meta.icon} size={16} color={colors.onAccent} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }}>{meta.label}</Text>
              <Text style={{ fontSize: 11, color: colors.ink4 }}>Metered AI call</Text>
            </View>
          </View>
          {/* Full reference id (the ledger row's DB primary key) — wraps so the
              whole UUID is visible, and is selectable for copy. */}
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 7, marginTop: 3 }}>
            <Icon name="tag" size={12} color={colors.ink4} />
            <Text style={{ fontSize: fontSize.xs, color: colors.ink3 }}>Ref ID</Text>
            <Text selectable style={{ flex: 1, textAlign: 'right', fontFamily: font.mono, fontSize: 11, lineHeight: 15, color: colors.ink2, fontWeight: fontWeight.semibold }}>{entry.id}</Text>
          </View>
          <DetailRow label="Triggered by" value={person} icon="user" />
          <DetailRow label="Model" value={entry.model ?? '—'} icon="box" mono />
          <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 8 }} />
          <DetailRow label="Input tokens" value={fmt(entry.inputTokens)} mono />
          <DetailRow label="Output tokens" value={fmt(entry.outputTokens)} mono />
          <DetailRow label="Total tokens" value={fmt(entry.totalTokens)} mono bold />
          <View style={{ height: 1, backgroundColor: colors.line, marginVertical: 8 }} />
          <DetailRow label="Exact time" value={fmtDateTime(entry.createdAt)} icon="clock" />
        </View>
      )}

      {/* serial number */}
      <View style={colStyle(col('sn'))}>
        <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink4 }}>{serial}</Text>
      </View>
      {/* purpose */}
      <View style={[colStyle(col('purpose')), { flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
        <View style={{ width: 30, height: 30, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line }}>
          <Icon name={meta.icon} size={15} color={colors.ink2} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }} numberOfLines={1}>{meta.short}</Text>
          <Text style={{ fontSize: 11, color: colors.ink4 }} numberOfLines={1}>{meta.label}</Text>
        </View>
      </View>
      {/* ref id — the ledger row's DB identifier (short form) */}
      <View style={colStyle(col('ref'))}>
        <Text style={{ fontFamily: font.mono, fontSize: 11, letterSpacing: 0.3, color: colors.ink3 }} numberOfLines={1}>{shortRef(entry.id)}</Text>
      </View>
      {/* model */}
      <View style={colStyle(col('model'))}>
        <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3 }} numberOfLines={1}>{entry.model ?? '—'}</Text>
      </View>
      {/* input / output / total — right-aligned via the shared column wrapper */}
      <View style={colStyle(col('input'))}>
        <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>{fmt(entry.inputTokens)}</Text>
      </View>
      <View style={colStyle(col('output'))}>
        <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink3 }}>{fmt(entry.outputTokens)}</Text>
      </View>
      <View style={colStyle(col('total'))}>
        <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }}>{fmt(entry.totalTokens)}</Text>
      </View>
      {/* user */}
      <View style={colStyle(col('user'))}>
        <Text style={{ fontSize: fontSize.xs, color: entry.email ? colors.ink2 : colors.ink4 }} numberOfLines={1}>{person}</Text>
      </View>
      {/* exact time */}
      <View style={colStyle(col('time'))}>
        <Text style={{ fontSize: fontSize.xs, color: colors.ink2 }}>{fmtDate(entry.createdAt)}</Text>
        <Text style={{ fontFamily: font.mono, fontSize: 11, color: colors.ink4 }}>{fmtTime(entry.createdAt)}</Text>
      </View>
    </View>
  );
}

function DetailRow({ label, value, icon, mono, bold }: { label: string; value: string; icon?: string; mono?: boolean; bold?: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, marginTop: 3 }}>
      {icon ? <Icon name={icon} size={12} color={colors.ink4} /> : <View style={{ width: 12 }} />}
      <Text style={{ fontSize: fontSize.xs, color: colors.ink3, flex: 1 }}>{label}</Text>
      <Text style={{ fontFamily: mono ? font.mono : font.sans, fontSize: fontSize.xs, fontWeight: bold ? fontWeight.bold : fontWeight.semibold, color: bold ? colors.ink : colors.ink2 }} numberOfLines={1}>{value}</Text>
    </View>
  );
}

function HeaderCell({ label, style }: { label: string; style?: object }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  return <Text style={[{ fontSize: 11, fontWeight: fontWeight.semibold, letterSpacing: 0.4, textTransform: 'uppercase', color: colors.ink4 }, style]}>{label}</Text>;
}

/* ── screen ───────────────────────────────────────────────────────────── */

export default function AiUsageScreen() {
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const { isMobile } = useBreakpoints();

  const [data, setData] = useState<AiUsageDetail | null>(null);
  const [days, setDays] = useState(30);
  const [mode, setMode] = useState<ChartMode>('io');
  const [op, setOp] = useState<AiUsageOpFilter>('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const reqIdRef = useRef(0);
  const fetchData = useCallback((d: number, o: AiUsageOpFilter, pg: number) => {
    const myReq = ++reqIdRef.current;
    const controller = new AbortController();
    getAiUsageDetail({ days: d, op: o, page: pg, pageSize: PAGE_SIZE }, controller.signal)
      .then((r) => { if (myReq === reqIdRef.current) setData(r); })
      .catch(() => { /* keep prior */ });
    return () => controller.abort();
  }, []);

  const refresh = useCallback(() => fetchData(days, op, page), [fetchData, days, op, page]);
  useFocusEffect(refresh);

  // Live: refetch (debounced) when the workspace rev bumps — e.g. a new AI call.
  const isFocused = useIsFocused();
  const { rev } = useWorkspaceStats();
  const lastRev = useRef(rev);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    if (!isFocused) { lastRev.current = rev; return; }
    if (rev === lastRev.current) return;
    lastRev.current = rev;
    if (timer.current) clearTimeout(timer.current);
    // 1.2s, matching the other live screens: one user action bumps `rev` several
    // times server-side (record + consent + profile + AI spend), so a narrow
    // window let each bump land as its own refetch.
    timer.current = setTimeout(() => refreshRef.current(), 1_200);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [rev, isFocused]);

  const loading = data === null;
  const summary = data?.summary;
  const series = data?.series ?? [];
  // Peak day → a "nice" axis ceiling so gridlines land on round numbers.
  const peak = useMemo(() => Math.max(0, ...series.map((p) => pointTotalFor(p, mode))), [series, mode]);
  const chartMax = niceCeil(peak || 1);
  const GRID = 4; // gridline intervals
  const ticks = useMemo(() => Array.from({ length: GRID + 1 }, (_, i) => Math.round((chartMax / GRID) * (GRID - i))), [chartMax]);
  const daysWithData = useMemo(() => series.filter((p) => pointTotalFor(p, mode) > 0).length, [series, mode]);
  const mounted = useMounted();

  const colorsBy = {
    input: colors.accent,
    output: colors.teal,
    ops: { jd_extract: colors.accent, resume_extract: colors.teal, rerank: colors.ink4 } as Record<string, string>,
  };

  const entries = data?.entries.items ?? [];
  const total = data?.entries.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasAny = (summary?.totalCalls ?? 0) > 0;
  const chartHeight = isMobile ? 300 : 240;
  const AXIS_W = 48;

  const onRange = (d: number) => { setDays(d); };
  const onOp = (o: AiUsageOpFilter) => { setOp(o); setPage(1); };

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 60 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 16 }}>
        {/* Header */}
        <View style={[styles.header, rise(20), isMobile && { flexDirection: 'column', alignItems: 'flex-start', gap: 12 }]}>
          <View style={{ gap: 4 }}>
            <Pressable onPress={() => navigateApp('dashboard')} style={[{ flexDirection: 'row', alignItems: 'center', gap: 6 }, web({ cursor: 'pointer' })]}>
              <Icon name="chev-l" size={14} color={colors.ink4} />
              <Text style={{ fontSize: fontSize.xs, color: colors.ink4 }}>Dashboard</Text>
            </Pressable>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <View style={{ width: 34, height: 34, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.accent }}>
                <Icon name="zap" size={17} color={colors.onAccent} />
              </View>
              <Text style={{ fontSize: 22, fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.4 }}>AI usage</Text>
            </View>
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>
              Prepaid credits · SK Meter · every metered call, by purpose · model · user
            </Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, height: 30, borderRadius: 999, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card }]}>
              <View style={{ position: 'relative', width: 8, height: 8 }}>
                <View pointerEvents="none" style={[{ position: 'absolute', width: 8, height: 8, borderRadius: 4, backgroundColor: colors.ink3 }, web({ animation: 'sk-pulse 2.4s ease-out infinite' })]} />
                <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.ink2 }} />
              </View>
              <Text style={{ fontSize: 11, color: colors.ink3 }}>Live</Text>
            </View>
            <Segmented options={RANGES} value={days} onChange={onRange} />
          </View>
        </View>

        {loading ? (
          <View style={{ paddingVertical: 80 }}><ActivityIndicator size="large" color={colors.accent} /></View>
        ) : !hasAny ? (
          <EmptyState
            icon="zap"
            title="No AI usage yet"
            subtitle="Once you extract a job description, ingest resumes or run a match, every metered AI call shows up here — by purpose, model and user."
          />
        ) : (
          <>
            {/* KPI tiles */}
            <View style={[{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }]}>
              <Kpi icon="zap" label="Total tokens" value={summary!.totalTokens} sub={`${fmt(summary!.totalCalls)} metered call${summary!.totalCalls === 1 ? '' : 's'}`} emphasis delay={40} />
              <Kpi icon="download" label="Input" value={summary!.inputTokens} sub={`${Math.round((summary!.inputTokens / Math.max(1, summary!.totalTokens)) * 100)}% of tokens`} delay={80} />
              <Kpi icon="upload" label="Output" value={summary!.outputTokens} sub={`${Math.round((summary!.outputTokens / Math.max(1, summary!.totalTokens)) * 100)}% of tokens`} delay={120} />
              <Kpi icon="sparkles" label="Rerank runs" value={summary!.rerankRuns} sub={`${fmt(summary!.models)} model${summary!.models === 1 ? '' : 's'} · ${fmt(summary!.users)} user${summary!.users === 1 ? '' : 's'}`} delay={160} />
            </View>

            {/* Comparison chart */}
            <View style={rise(180)}>
              <Panel
                title={`Daily token usage · last ${days} days`}
                action={isMobile ? undefined : <Segmented options={CHART_MODES} value={mode} onChange={setMode} />}
              >
                {/* Mobile: the 5 mode tabs can't fit the header, so they live in
                    a full-width horizontal scroll strip (visible scrollbar). */}
                {isMobile && (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator
                    {...({ dataSet: { skScroll: 'x' } } as object)}
                    style={{ marginBottom: 14 }}
                    contentContainerStyle={{ paddingBottom: 8 }}
                  >
                    <Segmented options={CHART_MODES} value={mode} onChange={setMode} />
                  </ScrollView>
                )}
                {/* legend + summary */}
                <View style={{ flexDirection: 'row', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                  {mode === 'io' ? (
                    <>
                      <LegendDot color={colorsBy.input} label={`Input · ${fmt(summary!.inputTokens)}`} />
                      <LegendDot color={colorsBy.output} label={`Output · ${fmt(summary!.outputTokens)}`} />
                    </>
                  ) : mode === 'op' ? (
                    Object.keys(OP_META).map((o) => (
                      <LegendDot key={o} color={colorsBy.ops[o]} label={`${opMeta(o).short} · ${fmt(data!.byOp.find((b) => b.op === o)?.totalTokens ?? 0)}`} />
                    ))
                  ) : (
                    <LegendDot color={colorsBy.ops[mode] ?? colorsBy.input} label={`${opMeta(mode).short} · ${fmt(data!.byOp.find((b) => b.op === mode)?.totalTokens ?? 0)}`} />
                  )}
                  <Text style={{ marginLeft: 'auto', fontSize: fontSize.xs, color: colors.ink4 } as object}>
                    {daysWithData} active day{daysWithData === 1 ? '' : 's'} · peak {fmt(peak)} tok
                  </Text>
                </View>

                {/* plot: y-axis + gridlines + bars */}
                <View style={{ flexDirection: 'row' }}>
                  {/* y-axis labels */}
                  <View style={{ width: AXIS_W, height: chartHeight, justifyContent: 'space-between', alignItems: 'flex-end', paddingRight: 8 }}>
                    {ticks.map((t) => (
                      <Text key={t} style={{ fontFamily: theme.font.mono, fontSize: 10, color: colors.ink4, lineHeight: 10, transform: [{ translateY: -5 }] }}>{fmtCompact(t)}</Text>
                    ))}
                  </View>
                  {/* plot area */}
                  <View style={{ flex: 1 }}>
                    <View style={{ height: chartHeight, position: 'relative' }}>
                      {/* horizontal gridlines */}
                      {ticks.map((t, i) => (
                        <View
                          key={t}
                          pointerEvents="none"
                          style={{ position: 'absolute', left: 0, right: 0, top: (chartHeight / GRID) * i, height: 1, backgroundColor: i === GRID ? colors.lineStrong : colors.line, opacity: i === GRID ? 1 : 0.6 }}
                        />
                      ))}
                      {/* bars */}
                      <View style={{ height: chartHeight, flexDirection: 'row', alignItems: 'flex-end', gap: series.length > 45 ? 1 : 3 }}>
                        {series.map((p, i) => (
                          <ChartColumn
                            key={p.date} point={p} max={chartMax} mode={mode} height={chartHeight}
                            colorsBy={colorsBy} mounted={mounted} index={i} showLabel={daysWithData <= 12}
                            tipAlign={i >= series.length - 4 ? 'right' : i <= 3 ? 'left' : 'center'}
                          />
                        ))}
                      </View>
                    </View>
                    {/* x-axis date ticks */}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 }}>
                      <Text style={{ fontSize: 10, color: colors.ink4 }}>{series[0] ? new Date(series[0].date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</Text>
                      {series.length > 2 && (
                        <Text style={{ fontSize: 10, color: colors.ink4 }}>{new Date(series[Math.floor(series.length / 2)].date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</Text>
                      )}
                      <Text style={{ fontSize: 10, color: colors.ink4 }}>{series.length ? new Date(series[series.length - 1].date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}</Text>
                    </View>
                  </View>
                </View>
              </Panel>
            </View>

            {/* Breakdowns */}
            <View style={[{ flexDirection: isMobile ? 'column' : 'row', gap: 16, alignItems: 'flex-start' }, rise(220)]}>
              <View style={isMobile ? { width: '100%' } : { flex: 1 }}>
                <Panel title="By purpose">
                  <View style={{ gap: 14 }}>
                    {data!.byOp.length === 0 ? <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>No data.</Text> :
                      data!.byOp.map((b, i) => (
                        <MeterRow key={b.op} icon={opMeta(b.op).icon} label={opMeta(b.op).label} value={b.totalTokens} total={summary!.totalTokens} color={colorsBy.ops[b.op] ?? colors.accent} delay={60 + i * 60} />
                      ))}
                  </View>
                </Panel>
              </View>
              <View style={isMobile ? { width: '100%' } : { flex: 1 }}>
                <Panel title="By model">
                  <View style={{ gap: 14 }}>
                    {data!.byModel.length === 0 ? <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>No data.</Text> :
                      data!.byModel.map((b, i) => (
                        <MeterRow key={b.model} icon="box" label={b.model} value={b.totalTokens} total={summary!.totalTokens} color={colors.accent} delay={60 + i * 60} mono={`${fmt(b.totalTokens)}`} />
                      ))}
                  </View>
                </Panel>
              </View>
              <View style={isMobile ? { width: '100%' } : { flex: 1 }}>
                <Panel title="By user">
                  <View style={{ gap: 14 }}>
                    {data!.byUser.length === 0 ? <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>No data.</Text> :
                      data!.byUser.map((b, i) => (
                        <MeterRow key={b.userId ?? b.email ?? i} icon="user" label={b.email ?? 'Unattributed'} value={b.totalTokens} total={summary!.totalTokens} color={colors.teal} delay={60 + i * 60} />
                      ))}
                  </View>
                </Panel>
              </View>
            </View>

            {/* Detailed log */}
            <View style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }, rise(260)]}>
              <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                {OP_FILTERS.map((f) => {
                  const active = op === f.key;
                  return (
                    <Pressable
                      key={f.key}
                      onPress={() => onOp(f.key)}
                      style={[
                        { flexDirection: 'row', alignItems: 'center', gap: 6, height: 32, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: active ? colors.accent : colors.line, backgroundColor: active ? colors.accentSoft : colors.card },
                        web({ cursor: 'pointer', transition: 'background 130ms ease, border-color 130ms ease' }),
                      ]}
                    >
                      <Text style={{ fontSize: fontSize.sm, fontWeight: active ? fontWeight.semibold : fontWeight.medium, color: active ? colors.accentInk : colors.ink2 }}>{f.label}</Text>
                    </Pressable>
                  );
                })}
              </View>
              {pageCount > 1 && <Pagination page={page} pageCount={pageCount} onChange={setPage} />}
            </View>

            <View style={rise(300)}>
              {entries.length === 0 ? (
                <Panel flush>
                  <EmptyState
                    icon={op === 'all' ? 'zap' : opMeta(op).icon}
                    title={op === 'all' ? 'No calls yet' : `No ${opMeta(op).short.toLowerCase()} calls`}
                    subtitle={op === 'all'
                      ? 'Metered AI calls will appear here as you use the workspace.'
                      : `Nothing recorded for this purpose yet. Show every call, or trigger a ${opMeta(op).short.toLowerCase()} action.`}
                    action={op === 'all' ? undefined : { label: 'Show all calls', icon: 'check', variant: 'secondary', onPress: () => onOp('all') }}
                  />
                </Panel>
              ) : isMobile ? (
                // Mobile: gapped column of stacked cards — the 9-column log can't
                // fit at phone widths.
                <View style={{ gap: 12 }}>
                  {entries.map((e, i) => <LogRow key={e.id} entry={e} serial={(page - 1) * PAGE_SIZE + i + 1} />)}
                </View>
              ) : (
                <Panel flush>
                  <View style={{ flexDirection: 'row', gap: GAP, paddingHorizontal: 16, paddingVertical: 11, backgroundColor: colors.paper2, borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>
                    {COLUMNS.map((c) => (
                      <View key={c.key} style={colStyle(c)}><HeaderCell label={c.label} /></View>
                    ))}
                  </View>
                  {entries.map((e, i) => <LogRow key={e.id} entry={e} serial={(page - 1) * PAGE_SIZE + i + 1} />)}
                </Panel>
              )}
            </View>
          </>
        )}
      </View>
    </ScrollView>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  const { theme } = useTheme();
  const { colors, fontSize } = theme;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
      <View style={{ width: 9, height: 9, borderRadius: 3, backgroundColor: color }} />
      <Text style={{ fontSize: fontSize.xs, color: colors.ink3 }}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
});
